<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('projectProfile.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('projectProfile.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="ts-flex ts-justify-between">
          <div class="ts-flex ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
          </div>
          <div>
            <Tooltip placement="left" max-width="300">
              <Icon type="md-help-circle" size="20" />
              <template #content>
                <p class="ts-text-xs">{{ $t('projectProfile.help') }}</p>
              </template>
            </Tooltip>
          </div>
        </div>
      </ts-card-wrapper>
      <ts-table-filter
        :columns="columns"
        :data="resources"
        :loading="loading"
        @search="search"
      >
        <template slot-scope="{ row }" slot="project">
          <div
            class="ts-flex ts-space-x-2 ts-items-center ts-py-2 hover:ts-text-gray-400 ts-text-blue-600 ts-cursor-pointer"
            @click.prevent="onView(row)"
          >
            <div>
              <p class="ts-mb-0 ts-whitespace-no-wrap">
                {{ row.project_name }}
              </p>
            </div>
          </div>
        </template>
        <template slot-scope="{ row }" slot="customer">
          <div>
            <p class="ts-m-0 ts-capitalize ts-whitespace-no-wrap">
              {{ row.customer_name }}
            </p>
          </div>
        </template>
        <template slot-scope="{ row }" slot="estimated">
          <p class="ts-mb-0 ts-whitespace-no-wrap">
            {{ row.estimated_start_date }} {{ row.estimated_end_date }}
          </p>
        </template>
        <template slot-scope="{ row }" slot="actual">
          <p class="ts-mb-0 ts-whitespace-no-wrap">
            {{ row.actual_start_date }} {{ row.actual_end_date }}
          </p>
        </template>
        <template slot-scope="{ row }" slot="progress">
          <Progress :percent="percentage(row)" status="active"></Progress>
        </template>
        <template slot-scope="{ row }" slot="action">
          <div class="ts-flex ts-space-x-2">
            <a
              href="#"
              class="text-primary"
              @click.prevent="createNewTask(row)"
              v-tooltip="$t('projectProfile.createNewTask')"
            >
              <Icon type="ios-menu" size="20" />
            </a>
            <a
              href="#"
              class="text-info"
              @click.prevent="onView(row)"
              v-tooltip="$t('view')"
            >
              <Icon type="md-eye" size="20" />
            </a>
            <a
              href="#"
              class="text-primary"
              @click.prevent="onEdit(row)"
              v-tooltip="$t('edit')"
            >
              <Icon type="ios-create" size="20" />
            </a>
            <a
              href="#"
              class="text-danger"
              v-tooltip="$t('delete')"
              @click="deleteRecord(row)"
              :disabled="row._deleting"
            >
              <i class="fas fa-circle-notch fa-spin" v-if="row._deleting"></i>
              <Icon type="ios-trash" size="20" v-else />
            </a>
          </div>
        </template>
      </ts-table-filter>
      <div class="ts-flex ts-items-center ts-justify-end ts-p-4">
        <ts-i-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-i-pagination>
      </div>
      <a-modal
        v-if="showModal"
        v-model="showModal"
        :title="$t('projectProfile.pageTitle')"
        :footer="null"
        :centered="true"
        :maskClosable="false"
        :zIndex="1000"
        width="50%"
        @cancel="onCancel"
      >
        <Form @cancel="onCancel" />
      </a-modal>
      <a-modal
        v-if="showUpdateForm"
        v-model="showUpdateForm"
        :title="$t('projectProfile.pageTitle')"
        :footer="null"
        :centered="true"
        :maskClosable="false"
        :zIndex="1000"
        width="50%"
        @cancel="onCancelUpdate"
      >
        <UpdateForm @cancel="onCancelUpdate" :updateData="updateData" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'
import Form from './form.vue'
import UpdateForm from './update-form.vue'
export default {
  components: {
    Form,
    UpdateForm
  },
  data() {
    return {
      showModal: false,
      showUpdateForm: false,
      view_panel: false,
      show_id: undefined,
      loading: false,
      data: [],
      value: [],
      fetching: false,
      updateData: [],
      status: []
    }
  },
  computed: {
    ...mapState('projectMgt/projectProfile', ['resources', 'pagination']),
    percentage: () => row => {
      if (row.task_completed_count) {
        return Math.round((row.task_completed_count / row.tasks_count) * 100)
      }
      return 0
    },
    columns() {
      return [
        // {
        //   title: this.$t('projectProfile.code'),
        //   key: 'project_code',
        //   keySearch: 'project_code',
        //   search: true,
        //   minWidth: 30
        // },
        {
          title: this.$t('projectProfile.project'),
          slot: 'project',
          keySearch: 'project_name',
          minWidth: 80
        },
        {
          title: this.$t('projectProfile.status'),
          key: 'project_status',
          keySearch: 'project_status',
          minWidth: 80
        },
        {
          title: this.$t('projectProfile.priority'),
          key: 'label',
          keySearch: 'pri.label',
          minWidth: 80
        },
        {
          title: this.$t('projectProfile.owner'),
          key: 'owner_name_en',
          keySearch: 'emp.employee_name_en',
          minWidth: 80
        },
        {
          title: this.$t('projectProfile.customer'),
          slot: 'customer',
          keySearch: 'customer_name',
          minWidth: 80
        },
        {
          title: this.$t('projectProfile.customerPhoneNumber'),
          key: 'customer_contact_number',
          keySearch: 'contact_number',
          minWidth: 80
        },
        {
          title: this.$t('projectProfile.estimated'),
          slot: 'estimated',
          minWidth: 80
        },
        {
          title: this.$t('projectProfile.actual'),
          slot: 'actual',
          minWidth: 80
        },
        {
          title: this.$t('projectProfile.progressing'),
          slot: 'progress',
          keySearch: 'completion',
          searchType: 'number_operation',
          minWidth: 140
        },
        {
          title: this.$t('projectProfile.createdBy'),
          key: 'created_by_name',
          keySearch: 'empc.employee_name_en',
          minWidth: 80
        },
        {
          title: this.$t('actions'),
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 130
        }
      ]
    }
  },
  created() {
    this.fetchData()
    this.fetchModel()
  },
  methods: {
    ...mapActions('projectMgt/projectProfile', [
      'fetch',
      'destroy',
      'formViewModel'
    ]),
    search(newValue) {
      this.$store.commit('projectMgt/projectProfile/SET_SEARCH', newValue)
      this.$store.commit('projectMgt/projectProfile/RESET_CURRENT_PAGE')
      this.fetchData()
    },
    addNew() {
      this.showModal = true
    },
    fetchData(attributes) {
      this.loading = true
      this.fetch(attributes)
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    fetchModel() {
      this.formViewModel({
        fnName: 'status'
      })
        .then(response => {
          this.status = response.data.status
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    handleChange(value) {
      Object.assign(this, {
        value,
        data: [],
        fetching: false
      })
    },
    onEdit(record) {
      this.showUpdateForm = true
      this.updateData = record
    },
    onCancel() {
      this.showModal = false
      this.fetchData()
    },
    onCancelUpdate() {
      this.showUpdateForm = false
      this.updateData = []
      this.fetchData()
    },
    deleteRecord(record) {
      this.$confirm({
        title: this.$t('are_you_sure_to_delete'),
        okText: this.$t('yes'),
        okType: 'danger',
        cancelText: this.$t('no'),
        centered: true,
        onOk: () => {
          return this.destroy(record.project_id)
            .then(response => {
              this.$toasted.success(response.message)
              this.fetchData()
              return Promise.resolve(response)
            })
            .catch(error => {
              this.$toasted.error(error.message)
              return Promise.reject(error)
            })
        },
        onCancel() {}
      })
    },
    onView(record) {
      // this.show_id =
      this.$router.push({
        name: 'project-profile-view',
        params: { id: record.project_id }
      })
      // this.view_panel = true
    },
    createNewTask(row) {
      this.$router.push({
        name: 'task-create',
        query: {
          project_id: row.project_id
        }
      })
    }
  },
  watch: {
    search: debounce(function() {
      this.fetchData()
    }, 500)
  }
}
</script>

<style scoped>
.ant-select-selection {
  border: 1px solid #e5e5e5 !important;
}
</style>
